
import { useEffect, useRef, useState } from 'react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

import { listCreateOrderOfBillings as  billingListApi } from "../graphql/queries";
import fetchItemsNextToken from "../function/fetchItemsNextTokenZau";

import './PrintReport.scss';

const url = new URL(window.location.href);
const initial_year = url.searchParams.get("year");
const initial_status = url.searchParams.get("type");

const months = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC", "TOTAL"];

/**
 * Parses a JSON string into an object.
 *
 * @param {string} obj - The JSON string to parse.
 * @returns {object} - The parsed object.
 */
function parseObject(obj) {
    return JSON.parse(obj);
}

/**
 * Formats the data based on the given month and item.
 *
 * @param {object} data - The data object.
 * @param {string} month - The month to retrieve data from.
 * @param {string} item - The item to retrieve data for.
 * @returns {string|null} - The formatted data or null if not found.
 */
function formatData(data, month, item) {
    if (data && data[month] && data[month][item]) {
        return Number(data[month][item].toFixed(2)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    return null;
}

/**
 * Formats the data count item.
 * 
 * @param {object} data - The data object.
 * @param {string} month - The month value.
 * @param {string} item - The item value.
 * @returns {number|null} - The formatted data count item or null if not found.
 */
function formatDataCountItem(data, month, item) {
    if (data && data[month] && data[month][item]) {
        return Number(data[month][item].toFixed(2));
    }
    return null;
}

function PrintReport() {
    const [data, setData] = useState(null);
    const [countData, setcountData] = useState(null);
    const pdfRef = useRef();


    useEffect(() => {
        async function fetchItems() {
            try {

                let queryData = {
                    query: billingListApi,
                        variables: {
                            filter: {
                                status: { eq: "15" },
                                isDeleted: { ne: "1" },
                                isDeny: { eq: "1" },
                            },
                            limit: 1000,
                        },
                }

                
                let queryDat1 = {
                    query: billingListApi,
                        variables: {
                            filter: {
                                or: [{ status: { eq: "4" }}, { status: { eq: "5" }}],
                                isDeleted: { ne: "1" },
                            },
                            limit: 1000,
                        },
                }

                // Make the requests in parallel
                let [result, _result] = await Promise.all([
                    fetchItemsNextToken(queryData),
                    fetchItemsNextToken(queryDat1)
                ]);

                const year = Number(initial_year); 

                let new_result = [...result, ..._result];

                new_result = new_result.filter(item => new Date(item.createdAt).getFullYear() === year);


                /**
                 * Groups the items by month and item count type.
                 *
                 * @param {Array} result - The array of items to be grouped.
                 * @returns {Object} - The object containing the grouped items.
                 */
                const groupByMonthAndItemCountType = new_result.reduce((acc, item) => {
                    const month = new Date(item.createdAt).toLocaleString('default', { month: 'short' }).toUpperCase();

                    if (!item.typeofapplication) item.typeofapplication = "LC";
                    
                    acc[month] = acc[month] || { total: 0 };
                    acc.total = acc.total || {};

                    if (item.status !== "15") {
                        acc[month][item.typeofapplication] = (acc[month][item.typeofapplication] || 0) + 1;
                        acc[month].total += 1;

                        acc.total[item.typeofapplication] = (acc.total[item.typeofapplication] || 0) + 1; // Increment the total for the typeofapplication
                        acc["grandtotalCount"] = (acc["grandtotalCount"] || 0) + 1; // Increment the grand total count
                    }
                   
                    if (item.status === "15") {
                        acc[month]['noc'] = (acc[month]['noc'] || 0) + 1;
                        acc.total['noc'] = (acc.total['noc'] || 0) + 1;
                        acc["grandtotalCountNoc"] = (acc["grandtotalCountNoc"] || 0) + 1; // Increment the grand total count
                    }

                    return acc;
                }, {});
                
                setcountData(groupByMonthAndItemCountType)

                /**
                 * Groups the result array by month and payment item, and calculates the total fees for each month and payment item.
                 *
                 * @param {Array} result - The array of items to be grouped and calculated.
                 * @returns {Object} - An object containing the grouped and calculated data.
                 */
                const groupByMonthAndItem = new_result.reduce((acc, item) => {
                    const month = new Date(item.createdAt).toLocaleString('default', { month: 'short' }).toUpperCase();
                    const paymentItems = parseObject(item.Payitem);
                
                    paymentItems.forEach(paymentItem => {
                        const fees = parseFloat(paymentItem.total);
                
                        if (!isNaN(fees)) {
                            acc[month] = acc[month] || { total: 0 };
                
                            // Check if the current paymentItem is the one you're looking for
                            if (paymentItem.paymentitem === 'Zoning - Application (Filling) Fee' || paymentItem.paymentitem === `Zoning - Processing Fee` || paymentItem.paymentitem === `Zoning - Motion for Reconsideration` || paymentItem.paymentitem === `Zoning - Certification`) {
                                acc[month][paymentItem.paymentitem] = (acc[month][paymentItem.paymentitem] || 0) + fees;
                                acc[month].total += fees; // Add fees to the total for the month
                                acc.total = (acc.total || 0) + fees; // Add fees to the total for all months
                            }
                        }
                    });
                
                    return acc;
                }, {});

                setData(groupByMonthAndItem);
              
            } catch (error) {
                console.log('error on fetchItems', error);
            }
        }
        fetchItems();
    }, [])

    function downloadThis() {
        document.getElementById("printOption").style.visibility = "hidden";
        document.getElementById("divToPrint").style.marginTop = "-30px";
        window.print();
        document.getElementById("printOption").style.visibility = "visible";
        document.getElementById("divToPrint").style.marginTop = "30px";
    }

    /**
     * Prints the document as a PDF.
     */
    const printDocument = () => {
        const input = pdfRef.current;
        html2canvas(input).then((canvas) => {
            const imgData = canvas.toDataURL("image/png");
            // Create a legal size document
            const pdf = new jsPDF("landscape", "mm", [216, 356]);
            const pdfWidth = pdf.internal.pageSize.getWidth();
            const pdfHeight = pdf.internal.pageSize.getHeight();
            const imgWidth = canvas.width;
            const imgHeight = canvas.height;
            const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
            const imgX = (pdfWidth - imgWidth * ratio) / 2;
            // Add a margin to the y position
            const imgY = ((pdfHeight - imgHeight * ratio) / 2) + 20;
            // Adjust the width and height of the image
            const adjustedImgWidth = imgWidth * ratio * 1.0; // 100% of the original width
            const adjustedImgHeight = imgHeight * ratio * 0.9; // 90% of the original height
            pdf.addImage(
                imgData,
                "PNG",
                imgX,
                imgY,
                adjustedImgWidth,
                adjustedImgHeight
            );
            pdf.save(`ReportLCForm-${initial_year}.pdf`);
        });
    };

    return (
        <>
             {data && (<div className="row">
                    <button
                        onClick={printDocument}
                        className="btn btn-primary btn-block"
                    >
                        Download
                    </button>
                </div>)
            }
            <div className="ReportLCForm" id="divToPrint" ref={pdfRef}>
                <div className="lc-header">
                    <div className="qc-header" style={{ fontWeight: "bold" }}>
                    <p>STATUS OF ZONING APPLICATIONS</p>
                    <p className="pb-1">BUILDING PERMIT PURPOSES</p>
                    <p>JANUARY {`${initial_year}`} - DECEMBER {`${initial_year}`}</p>
                    </div>
                </div>
                <div className="table-wrapper table-responsive">
                    <table className="mb-1" border="1" style={{ width: '100%' }}>
                        <tbody>
                            {Array.from({ length: 22 }, (_, rowIndex) => (
                                <tr key={rowIndex}>
                                    {Array.from({ length: 14 }, (_, colIndex) => {
                                        if (rowIndex === 0  && colIndex === 1) {
                                            return <td key={colIndex} colSpan={13} style={{ width: '100px', height: '50px', textAlign:"center" }}>MONTH</td>
                                        }

                                        if (rowIndex === 12 && colIndex === 1) {
                                            return <td key={colIndex} colSpan={13} style={{ width: '100px', height: '50px', textAlign:"center" }}>AMOUNT</td>
                                        }
                                        
                                        if ((rowIndex === 0 || rowIndex === 12) && colIndex > 1) {
                                            return null;
                                        }
                                        return (
                                            <td key={colIndex} style={{ width: colIndex === 0 ? '250px' : '200px', height: '50px' }}>
                                                {(rowIndex === 1 || rowIndex === 13) && colIndex >= 1 ? months[colIndex - 1] : null}
                                                {rowIndex === 0 && colIndex === 0 ? 'TYPE OF CLEARANCE' : null}
                                                {rowIndex === 1 && colIndex === 0 ? 'RELEASED' : null}
                                                {rowIndex === 3 && colIndex === 0 ? 'LOCATIONAL CLEARANCE' : null}
                                                {rowIndex === 3 && months[colIndex - 1] 
                                                    ? (colIndex - 1 === months.length - 1 
                                                        ? formatDataCountItem(countData, months[colIndex - 1], "LC") 
                                                        : formatDataCountItem(countData, months[colIndex - 1], "LC") || 0)
                                                    : null
                                                }
                                                {rowIndex === 3 && colIndex === 13 ? countData && countData["total"]["LC"] : null}
                                                {rowIndex === 4 && colIndex === 0 ? 'SPECIAL USE OF PERMIT' : null}
                                                {rowIndex === 4 && months[colIndex - 1] 
                                                    ? (colIndex - 1 === months.length - 1 
                                                        ? formatDataCountItem(countData, months[colIndex - 1], "LCSUP") 
                                                        : formatDataCountItem(countData, months[colIndex - 1], "LCSUP") || 0)
                                                    : null
                                                }
                                                {rowIndex === 4 && colIndex === 13 ? countData && countData["total"]["LCSUP"] : null}
                                                {rowIndex === 5 && colIndex === 0 ? 'CERTIFICATE OF EXCEPTIONS' : null}
                                                {rowIndex === 5 && months[colIndex - 1] 
                                                    ? (colIndex - 1 === months.length - 1 
                                                        ? formatDataCountItem(countData, months[colIndex - 1], "COE") 
                                                        : formatDataCountItem(countData, months[colIndex - 1], "COE") || 0)
                                                    : null
                                                }
                                                {rowIndex === 5 && colIndex === 13 ? countData && countData["total"]["COE"] : null}
                                                {rowIndex === 6 && colIndex === 0 ? 'CERT. NON-CONFORMANCE' : null}
                                                {rowIndex === 7 && colIndex === 0 ? 'TOTAL' : null}
                                                {rowIndex === 7 && colIndex === 13 ? countData && countData["grandtotalCount"] : null}
                                                {rowIndex === 9 && colIndex === 0 ? 'COMPLIANCE' : null}
                                                {rowIndex === 10 && colIndex === 0 ? 'NOTICE OF ACTIONS' : null}
                                                {rowIndex === 10 && months[colIndex - 1] 
                                                    ? (colIndex - 1 === months.length - 1 
                                                        ? formatDataCountItem(countData, months[colIndex - 1], "noc") 
                                                        : formatDataCountItem(countData, months[colIndex - 1], "noc") || 0)
                                                    : null
                                                }
                                                {rowIndex === 10 && colIndex === 13 ? countData && countData["total"]["noc"] : null}
                                                {rowIndex === 12 && colIndex === 0 ? 'ESTIMATED ZONING' : null}
                                                {rowIndex === 13 && colIndex === 0 ? 'REVENUES' : null}
                                                {rowIndex === 15 && colIndex === 0 ? 'FILLING FEE' : null}
                                                {rowIndex === 15 && months[colIndex - 1] 
                                                    ? (colIndex - 1 === months.length - 1 
                                                        ? formatData(data, months[colIndex - 1], "Zoning - Application (Filling) Fee") 
                                                        : formatData(data, months[colIndex - 1], "Zoning - Application (Filling) Fee") || 0)
                                                    : null
                                                }
                                                {rowIndex === 16 && colIndex === 0 ? 'PROCESSING FEE' : null}
                                                {rowIndex === 16 && months[colIndex - 1] 
                                                    ? (colIndex - 1 === months.length - 1 
                                                        ? formatData(data, months[colIndex - 1], "Zoning - Processing Fee") 
                                                        : formatData(data, months[colIndex - 1], "Zoning - Processing Fee") || 0)
                                                    : null
                                                }
                                                {rowIndex === 17 && colIndex === 0 ? 'RECONSIDERATIONS' : null}
                                                {rowIndex === 17 && months[colIndex - 1] 
                                                    ? (colIndex - 1 === months.length - 1 
                                                        ? formatData(data, months[colIndex - 1], "Zoning - Motion for Reconsideration") 
                                                        : formatData(data, months[colIndex - 1], "Zoning - Motion for Reconsideration") || 0)
                                                    : null
                                                }
                                                {rowIndex === 18 && colIndex === 0 ? 'ZONING CERTIFICATE FEE' : null}
                                                {rowIndex === 18 && months[colIndex - 1] 
                                                    ? (colIndex - 1 === months.length - 1 
                                                        ? formatData(data, months[colIndex - 1], "Zoning - Certification") 
                                                        : formatData(data, months[colIndex - 1], "Zoning - Certification") || 0)
                                                    : null
                                                }
                                                {rowIndex === 21 && colIndex === 0 ? 'TOTAL' : null}
                                                {rowIndex === 21 && months[colIndex - 1] 
                                                    ? (colIndex - 1 === months.length - 1 
                                                        ? formatData(data, months[colIndex - 1], "total") 
                                                        : formatData(data, months[colIndex - 1], "total") || 0)
                                                    : null
                                                }
                                                {rowIndex === 21 && colIndex === 13 ? data && Number(data["total"].toFixed(2)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : null}
                                            </td>
                                        )
                                    })}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </>
    )
}


export default PrintReport;